/**
 * PeekPanel — slide-in right side detail for a single ticket on the
 * Triage inbox. Lets staff scan a ticket without leaving the list.
 *
 * INTERACTION_SPEC §9 — Drawer: slide in from right, 240ms; content
 * underneath stays interactive. We're not modal — selection,
 * filtering, and j/k nav (Stage 3) all keep working with the panel
 * open. Esc closes via TriageInbox's global hotkey.
 *
 * The panel currently shows ticket meta (contract, subject, priority,
 * status, SLA snapshot) + an "Open" button to navigate to the full
 * ticket detail. The richer "Conversation" / "SLA clocks" / "Hours"
 * blocks from the design handoff are deferred until the peek's value
 * is proven; the right panel of TicketDetail.tsx already covers those.
 */

import { ExternalLink, X } from 'lucide-react';

import type { Ticket } from '@beacon/core';

import { ContractLogo } from '@/components/beacon/contract-logo.js';
import { Priority } from '@/components/beacon/priority.js';
import { SlaTicker } from '@/components/beacon/sla-ticker.js';
import { Status } from '@/components/beacon/status.js';
import { Avatar } from '@/components/beacon/avatar.js';
import { Button } from '@/components/ui/button.js';
import { useContractsById } from '@/hooks/use-contracts.js';
import { contractDisplay } from '@/lib/contract-display.js';

interface PeekPanelProps {
  ticket: Ticket;
  onClose: () => void;
  onOpen: () => void;
}

export function PeekPanel({ ticket, onClose, onOpen }: PeekPanelProps) {
  const contractsById = useContractsById();
  const contract = contractsById.get(ticket.contractId);
  const display = contract
    ? contractDisplay(contract)
    : { tag: '??', color: '#4A4F5A' };

  return (
    <aside
      role="complementary"
      aria-label="Ticket peek"
      className="w-[340px] shrink-0 border-l border-ink-600 bg-ink-850 flex flex-col"
    >
      <div className="px-4 h-12 border-b border-ink-600 flex items-center gap-2 shrink-0">
        <span className="text-[10.5px] uppercase tracking-[0.12em] text-ink-300 font-semibold">
          Peek
        </span>
        <span className="ml-auto flex items-center gap-1">
          <button
            onClick={onOpen}
            aria-label="Open ticket in full view"
            title="Open ticket"
            className="h-7 w-7 inline-flex items-center justify-center rounded text-ink-300 hover:text-ink-100 hover:bg-ink-700 focus-ring"
          >
            <ExternalLink size={14} />
          </button>
          <button
            onClick={onClose}
            aria-label="Close peek"
            title="Close (Esc)"
            className="h-7 w-7 inline-flex items-center justify-center rounded text-ink-300 hover:text-ink-100 hover:bg-ink-700 focus-ring"
          >
            <X size={14} />
          </button>
        </span>
      </div>

      <div className="flex-1 overflow-auto px-4 py-4 space-y-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <ContractLogo contract={display} size={22} />
            <span className="text-[13px] text-ink-200 truncate">
              {contract?.name ?? '—'}
            </span>
            <span className="mono text-[11.5px] text-ink-300 ml-auto">
              {ticket.number}
            </span>
          </div>
          <h2 className="text-[15px] font-semibold text-ink-100 leading-snug mb-2">
            {ticket.subject}
          </h2>
          <div className="flex items-center flex-wrap gap-1.5">
            <Priority p={ticket.priority} />
            <Status s={ticket.status} />
            <SlaTicker ticket={ticket} contract={contract} hidePlaceholder />
          </div>
        </div>

        <div className="h-px bg-ink-600" />

        <div>
          <PaneLabel>Assignee</PaneLabel>
          <div className="bg-ink-800 border border-ink-600 rounded-md px-2 h-9 flex items-center gap-2">
            {ticket.assigneeUserId ? (
              <>
                <Avatar
                  name={ticket.assigneeUserId.replace('user-', '')}
                  size={22}
                />
                <span className="text-[13px] text-ink-100 flex-1 truncate">
                  {ticket.assigneeUserId.replace('user-', '')}
                </span>
              </>
            ) : (
              <span className="text-[12.5px] text-ink-400 italic flex-1">
                Unassigned
              </span>
            )}
          </div>
        </div>

        <div>
          <PaneLabel>Created</PaneLabel>
          <div className="text-[12.5px] text-ink-200 mono">
            {new Date(ticket.createdAt).toLocaleString('en-AU', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </div>
        </div>

        {ticket.firstResponseAt && (
          <div>
            <PaneLabel>First response</PaneLabel>
            <div className="text-[12.5px] text-ink-200 mono">
              {new Date(ticket.firstResponseAt).toLocaleString('en-AU', {
                day: '2-digit',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </div>
          </div>
        )}
      </div>

      <div className="px-4 h-12 border-t border-ink-600 flex items-center shrink-0">
        <Button variant="primary" size="sm" onClick={onOpen} className="w-full">
          Open ticket
        </Button>
      </div>
    </aside>
  );
}

function PaneLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10.5px] uppercase tracking-[0.12em] text-ink-400 font-semibold mb-1.5">
      {children}
    </div>
  );
}
